import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Utensils, ShoppingBag, ChefHat, Users, Bike, Megaphone } from "lucide-react";
import { RestaurantsTab } from "@/components/admin/RestaurantsTab";
import { OrdersTab } from "@/components/admin/OrdersTab";
import { UsersTab } from "@/components/admin/UsersTab";
import { DriversTab } from "@/components/admin/DriversTab";
import { OwnersTab } from "@/components/admin/OwnersTab";
import { NotificationsTab } from "@/components/admin/NotificationsTab";

const TABS = [
  { id: "restaurants", icon: Utensils, label: "المطاعم" },
  { id: "orders", icon: ShoppingBag, label: "الطلبات" },
  { id: "users", icon: Users, label: "المستخدمون" },
  { id: "drivers", icon: Bike, label: "المناديب" },
  { id: "owners", icon: ChefHat, label: "أصحاب المطاعم" },
  { id: "notifications", icon: Megaphone, label: "الإشعارات" },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("restaurants");
  const [restaurants, setRestaurants] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [owners, setOwners] = useState([]);

  const loadRestaurants = () => api.get("/admin/restaurants").then(({ data }) => setRestaurants(data));
  const loadOrders = () => api.get("/admin/orders").then(({ data }) => setOrders(data));
  const loadUsers = () => api.get("/admin/users").then(({ data }) => setUsers(data));
  const loadDrivers = () => api.get("/admin/drivers").then(({ data }) => setDrivers(data));
  const loadOwners = () => api.get("/admin/restaurant-owners").then(({ data }) => setOwners(data));

  useEffect(() => {
    if (user?.is_admin) {
      loadRestaurants();
      loadOrders();
      loadUsers();
      loadDrivers();
      loadOwners();
    }
  }, [user]);

  // Auto-refresh drivers every 15s while the Drivers tab is open (for live map)
  useEffect(() => {
    if (!user?.is_admin || tab !== "drivers") return;
    const iv = setInterval(loadDrivers, 15000);
    return () => clearInterval(iv);
  }, [user, tab]);

  // Auto-refresh users every 30s while the Users tab is open (for last-seen)
  useEffect(() => {
    if (!user?.is_admin || tab !== "users") return;
    const iv = setInterval(loadUsers, 30000);
    return () => clearInterval(iv);
  }, [user, tab]);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#E05D3A] border-t-transparent rounded-full animate-spin" />
    </div>;
  }
  if (!user || !user.is_admin) return <Navigate to="/login" replace />;

  const counts = { restaurants: restaurants.length, orders: orders.length, users: users.length, drivers: drivers.length, owners: owners.length };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-black text-[#1A1A1A]">لوحة التحكم</h1>
          <p className="text-sm text-[#6B6B6B] mt-1">أهلاً، {user.name} · {user.email}</p>
        </div>
        <Badge className="bg-[#E05D3A] text-white">Admin</Badge>
      </div>

      <div className="flex gap-2 border-b border-[#EAEAEA] mb-6 overflow-x-auto">
        {TABS.map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-all whitespace-nowrap ${tab === id ? "border-[#E05D3A] text-[#E05D3A]" : "border-transparent text-[#6B6B6B]"}`}
            data-testid={`admin-tab-${id}`}
          >
            <Icon className="w-4 h-4 inline me-2" />
            {label}{counts[id] !== undefined && ` (${counts[id]})`}
          </button>
        ))}
      </div>

      {tab === "restaurants" && <RestaurantsTab restaurants={restaurants} reload={loadRestaurants} />}
      {tab === "orders" && <OrdersTab orders={orders} reload={loadOrders} />}
      {tab === "users" && <UsersTab users={users} reload={loadUsers} />}
      {tab === "drivers" && <DriversTab drivers={drivers} reload={loadDrivers} />}
      {tab === "owners" && <OwnersTab owners={owners} restaurants={restaurants} reload={loadOwners} />}
      {tab === "notifications" && <NotificationsTab />}
    </div>
  );
}
